import React, { useState, useEffect, useRef } from 'react';
import { User, Mail, Phone, MapPin, Camera, Save, ShoppingBag, Star, Pencil, AlertTriangle, Wallet } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import OrderHistory from '../components/user/OrderHistory';
import ComplaintsSection from '../components/user/ComplaintsSection';
import MyReviews from '../components/user/MyReviews';
import WalletSection from '../components/user/WalletSection';
import orderService from '../services/orderService';
import reviewService from '../services/reviewService';
import complaintService from '../services/complaintService';
import userService from '../services/userService';
import { sanitizeObject } from '../lib/sanitize';

const Profile = () => {
  const { user, isAuthenticated, updateProfile, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [isEditing, setIsEditing] = useState(false);
  const [orderCount, setOrderCount] = useState('—');
  const [reviewCount, setReviewCount] = useState('—');
  const [complaintCount, setComplaintCount] = useState('—');
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    const tab = searchParams.get('tab');
    return ['orders', 'reviews', 'complaints', 'wallet'].includes(tab) ? tab : 'orders';
  });

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (['orders', 'reviews', 'complaints', 'wallet'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);
  const fileInputRef = useRef(null);

  // Simple generic user avatar
  const defaultAvatar = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100' fill='%23888'%3E%3Ccircle cx='50' cy='50' r='50' fill='%23e8e8e8'/%3E%3Ccircle cx='50' cy='38' r='18' fill='%23aaa'/%3E%3Cpath d='M50 60c-20 0-36 12-36 27v13h72v-13c0-15-16-27-36-27z' fill='%23aaa'/%3E%3C/svg%3E";

  useEffect(() => {
    orderService.getMyOrders({ limit: 1 }).then(result => {
      if (result.success) setOrderCount(result.data.pagination.total);
    });
    reviewService.getMyReviews({ limit: 1 }).then(result => {
      if (result.success) setReviewCount(result.data.pagination.total);
    });
    complaintService.getMyComplaints({ limit: 1 }).then((result) => {
      if (result.success) setComplaintCount(result.data.pagination.total);
    });
    refreshUser();
  }, []);

  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: user?.address || ''
  });

  const handleAvatarClick = () => fileInputRef.current?.click();

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file'); return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be under 5MB'); return;
    }

    // Show instant preview
    const reader = new FileReader();
    reader.onloadend = () => setAvatarPreview(reader.result);
    reader.readAsDataURL(file);

    setAvatarUploading(true);
    const result = await userService.uploadAvatar(file);
    setAvatarUploading(false);

    if (result.success) {
      setAvatarPreview(null);
      await refreshUser();
    } else {
      setAvatarPreview(null);
      alert('Upload failed: ' + result.error);
    }
    e.target.value = '';
  };

  const handleSave = () => {
    updateProfile(sanitizeObject(formData));
    setIsEditing(false);
  };

  const stats = [
    { label: 'Orders', value: orderCount, icon: ShoppingBag },
    { label: 'Complaints', value: complaintCount, icon: AlertTriangle },
    { label: 'Reviews', value: reviewCount, icon: Star },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-12">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Profile Header */}
          <div className="bg-gradient-to-r from-[#2e4e18] to-[#3f6b20] rounded-2xl p-8 text-white mb-8">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="relative">
                <img
                  src={avatarPreview || user?.avatar || defaultAvatar}
                  alt={user?.name || 'User'}
                  className="w-24 h-24 rounded-full border-4 border-white/30 object-cover"
                />
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleAvatarChange}
                  accept="image/*"
                  className="hidden"
                />
                <button
                  onClick={handleAvatarClick}
                  disabled={avatarUploading}
                  className="absolute bottom-0 right-0 w-8 h-8 bg-[#548B2E] rounded-full flex items-center justify-center border-2 border-white hover:bg-[#3f6b20] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  title="Change profile picture"
                >
                  {avatarUploading
                    ? <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    : <Camera className="w-4 h-4" />
                  }
                </button>
              </div>
              <div className="text-center md:text-left">
                <h1 className="text-2xl font-bold mb-1">{user?.name}</h1>
                <p className="text-white/70">{user?.email}</p>
                <span className="inline-block mt-2 px-3 py-1 bg-white/20 rounded-full text-sm">
                  {user?.role === 'super_admin' ? 'Master Control Admin' : user?.role === 'admin' ? 'Administrator' : 'Member'}
                </span>
              </div>
              <div className="md:ml-auto flex gap-4">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center px-4">
                    <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-2">
                      <stat.icon className="w-5 h-5" />
                    </div>
                    <p className="text-2xl font-bold">{stat.value}</p>
                    <p className="text-xs text-white/70">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Profile Details */}
          <div className="bg-white rounded-2xl shadow-sm p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-800">Profile Information</h2>
              <button
                onClick={() => isEditing ? handleSave() : setIsEditing(true)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  isEditing 
                    ? 'bg-[#548B2E] text-white hover:bg-[#3f6b20]'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {isEditing ? (
                  <>
                    <Save className="w-4 h-4" />
                    Save Changes
                  </>
                ) : (
                  <>
                    <Pencil className="w-4 h-4" />
                    Edit Profile
                  </>
                )}
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <User className="w-4 h-4 inline mr-2" />
                  Full Name
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
                  />
                ) : (
                  <p className="px-4 py-3 bg-gray-50 rounded-lg text-gray-800">{user?.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Mail className="w-4 h-4 inline mr-2" />
                  Email Address
                </label>
                {isEditing ? (
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Enter your email address"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
                  />
                ) : (
                  <p className="px-4 py-3 bg-gray-50 rounded-lg text-gray-800">{user?.email || 'Not provided'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Phone className="w-4 h-4 inline mr-2" />
                  Phone Number
                </label>
                {isEditing ? (
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="Enter your phone number"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
                  />
                ) : (
                  <p className="px-4 py-3 bg-gray-50 rounded-lg text-gray-500">
                    {formData.phone || 'Not provided'}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="w-4 h-4 inline mr-2" />
                  Address
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Enter your address"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
                  />
                ) : (
                  <p className="px-4 py-3 bg-gray-50 rounded-lg text-gray-500">
                    {formData.address || 'Not provided'}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Orders & Reviews Tabs */}
          <div className="mt-8">
            {/* Tab Buttons */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setActiveTab('orders')}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-colors ${
                  activeTab === 'orders'
                    ? 'bg-[#2e4e18] text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                <ShoppingBag className="w-4 h-4" />
                Order History
                {orderCount !== '—' && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    activeTab === 'orders' ? 'bg-white/20' : 'bg-gray-100'
                  }`}>
                    {orderCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('reviews')}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-colors ${
                  activeTab === 'reviews'
                    ? 'bg-[#2e4e18] text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                <Star className="w-4 h-4" />
                My Reviews
                {reviewCount !== '—' && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    activeTab === 'reviews' ? 'bg-white/20' : 'bg-gray-100'
                  }`}>
                    {reviewCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('complaints')}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-colors ${
                  activeTab === 'complaints'
                    ? 'bg-[#2e4e18] text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                <AlertTriangle className="w-4 h-4" />
                Complaints
                {complaintCount !== '—' && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    activeTab === 'complaints' ? 'bg-white/20' : 'bg-gray-100'
                  }`}>
                    {complaintCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('wallet')}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-colors ${
                  activeTab === 'wallet'
                    ? 'bg-[#2e4e18] text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                <Wallet className="w-4 h-4" />
                BMF Coins
              </button>
            </div>

            {/* Tab Content */}
            {activeTab === 'orders' ? (
              <OrderHistory onReviewSubmitted={() => setReviewCount(prev => prev === '—' ? 1 : prev + 1)} />
            ) : activeTab === 'reviews' ? (
              <MyReviews />
            ) : activeTab === 'wallet' ? (
              <WalletSection />
            ) : (
              <ComplaintsSection />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;